/**
 * AudioActivityPlayer — Reproductor de audio para actividades de audio
 * Muestra botón play/pause, barra de progreso y contador de reproducciones.
 * Si audioUrl es null, muestra el audioText como fallback visible.
 */

import { useState, useRef } from 'react';
import { cn } from '@/utils/cn';
import styles from './AudioActivityPlayer.module.scss';

interface AudioActivityPlayerProps {
  audioUrl: string | null;
  audioText?: string;
  className?: string;
}

export function AudioActivityPlayer({ audioUrl, audioText, className }: AudioActivityPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!audioUrl) return;

    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.onended = () => {
        setIsPlaying(false);
        if (audioRef.current) audioRef.current.currentTime = 0;
      };
      audioRef.current.onerror = () => setIsPlaying(false);
    }

    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      void audioRef.current.play();
      setIsPlaying(true);
    }
  };

  if (!audioUrl) {
    return audioText ? (
      <p className={cn(styles.audioPlayer__fallback, className)}>{audioText}</p>
    ) : null;
  }

  return (
    <button
      type="button"
      className={cn(styles.audioPlayer__playBtn, className)}
      onClick={handlePlayPause}
      aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'}
    >
      {isPlaying ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </button>
  );
}
